import { defineStore } from 'pinia'
import userApi from '@/api/modules/user'
import { getInitials } from '@/global/helpers'

export const useUserStore = defineStore('user', {
  state: () => ({
    profile: null,
    isLoading: false,
    error: null,
  }),

  getters: {
    // Returns true if the user profile is loaded
    hasProfile: (state) => !!state.profile,

    firstName: (state) => {
      if (!state.profile) return ''

      const { firstName } = state.profile
      if (firstName) {
        return firstName
      }

      return ''
    },

    lastName: (state) => {
      if (!state.profile) return ''

      const { lastName } = state.profile
      if (lastName) {
        return lastName
      }

      return ''
    },

    companyName: (state) => {
      if (!state.profile) return ''

      const { companyName } = state.profile
      if (companyName) {
        return companyName
      }

      return ''
    },

    isBuyer: (state) => {
      if (!state.profile) return false
      return state.profile.companyType === 'Buyer'
    },

    profilePicture: (state) => {
      if (!state.profile) return ''

      const { avatarUrl } = state.profile
      if (avatarUrl) {
        return avatarUrl
      }

      return ''
    },

    email: (state) => {
      if (!state.profile) return ''

      const { email } = state.profile
      if (email) {
        return email
      }

      return ''
    },

    phoneNumber: (state) => {
      if (!state.profile) return ''

      const { phoneNumber } = state.profile
      if (phoneNumber) {
        return phoneNumber
      }

      return ''
    },

    userInitials: (state) => {
      if (!state.profile) return ''
      const { firstName, lastName } = state.profile

      return getInitials(firstName, lastName)
    },

    userId: (state) => {
      if (!state.profile) return null

      const { userId } = state.profile
      if (userId) {
        return userId
      }

      return null
    },

    availableCompanies: (state) => {
      if (!state.profile?.accessibleCompanies) return []

      return state.profile.accessibleCompanies.map((company) => ({
        id: company.id,
        name: company.name,
        selected: state.profile.companyId === company.id,
      }))
    },
  },

  actions: {
    /**
     * Fetch the current user's profile information from the API
     * @returns {Promise} Promise that resolves to the user profile
     */
    async fetchUserProfile() {
      this.isLoading = true
      this.error = null

      try {
        const userData = await userApi.getCurrentUser()
        if (userData) {
          this.profile = userData
        }
        return this.profile
      } catch (error) {
        this.error = error.message || 'Failed to fetch user profile'
        console.error('Error fetching user profile:', error)
        throw error
      } finally {
        this.isLoading = false
      }
    },

    /**
     * Reset the user store state
     */
    clearUserProfile() {
      this.profile = null
      this.isLoading = false
      this.error = null
    },

    /**
     * Check if there's a need to fetch the user profile
     */
    shouldFetchProfile() {
      return !this.profile && !this.isLoading
    },
  },
})
